import { EventListener, Event } from "silver";
import { toggleChildNodeAvailability, generateId } from "shared/functions";

export interface ExpanderChangeEvent extends Event {
	open: boolean;
	sender: Expander|undefined;
}

export type ExpanderOptions = {
	sourceOpenClass?: string;
	targetOpenClass?: string;
	slideDuration?: number;
};

function awaitAnimationFrame(): Promise<void> {
	return new Promise(r => requestAnimationFrame(() => r()));
}

export class Expander {

	public static readonly onChange = EventListener.create<ExpanderChangeEvent>("change");
	public readonly onChange = EventListener.create<ExpanderChangeEvent>("change");
	protected openValue: boolean = false;

	constructor(
		public readonly source: HTMLElement,
		public readonly target: HTMLElement,
		protected options: ExpanderOptions = { }
	) {
		if (!target.id) {
			target.id = generateId("expander-");
		}
		if (!source.id) {
			source.id = `${target.id}-source`;
		}
		if (!target.hasAttribute("role")) {
			target.setAttribute("role", "region");
		}
		target.setAttribute("aria-labelledby", source.id);
		source.setAttribute("aria-controls", target.id);
		source.addEventListener("click", () => {
			this.setOpen(!this.open, this);
		});
		this.handleStateChange(this.open);
	}

	public get open(): boolean {
		return this.openValue;
	}

	public set open(open: boolean) {
		this.setOpen(open);
	}

	private setOpen(open: boolean, sender?: Expander): void {
		this.handleStateChange(open);
		if (this.openValue === open) {
			return;
		}
		this.openValue = open;
		this.onChange.trigger({ open, sender });
		Expander.onChange.trigger({ open, sender });
	}

	protected handleStateChange(open: boolean): void {
		this.source.setAttribute("aria-expanded", open ? "true" : "false");
		toggleChildNodeAvailability(this.target, open, (child => child !== this.source));
		if (this.options.targetOpenClass) {
			this.target.classList.toggle(this.options.targetOpenClass, open);
		}
		if (this.options.sourceOpenClass) {
			this.source.classList.toggle(this.options.sourceOpenClass, open);
		}
		if (this.options.slideDuration) {
			this.slideAnimate(this.options.slideDuration, open);
		}
	}

	protected async slideAnimate(duration: number, open: boolean): Promise<void> {
		this.target.style.overflow = "hidden";
		if (!open) {
			this.target.style.maxHeight = "0";
			return;
		}
		this.target.style.transition = "none";
		this.target.style.maxHeight = "";
		await awaitAnimationFrame();
		const height = this.target.offsetHeight;
		this.target.style.maxHeight = "0";
		await awaitAnimationFrame();
		this.target.style.transition = `max-height ${duration}ms`;
		this.target.style.maxHeight = `${height}px`;
	}
}

